import { useCallback, useEffect, useRef, useState } from 'react'
import { type Theme } from '@opallabs/gem'
import { ThemeProvider } from '@opallabs/hooks'
import { Button, ButtonIconPosition, Icon, Pill } from '@opallabs/lapidary'
import { Connection, fetchConnection } from './api'
import * as styles from './styles'

type Integration = Readonly<{
  key: string
  name: string
  description: string
  // Same-origin monolith routes. Both inject the acting user + Opal from the
  // session, so no identity is passed here.
  connectPath: string
  statusPath: string
}>

const INTEGRATIONS: readonly Integration[] = [
  {
    key: 'sharepoint',
    name: 'Microsoft SharePoint',
    description:
      'Connect your Microsoft account to add SharePoint documents to posts and preview them with your own SharePoint permissions.',
    connectPath: '/integrations/sharepoint/connect',
    statusPath: '/integrations/sharepoint/accounts',
  },
]

const POPUP_POLL_MS = 500

type IntegrationConnectionsProps = Readonly<{
  /** Theme of the surface this is mounted in; it renders its own provider. */
  theme: Theme
}>

/**
 * Lets the current user connect (or reconnect) their personal accounts on
 * third-party integrations. Mounted by the legacy "Edit My Settings" modal's
 * Integrations pane (`SettingsEditor.Integrations` in `app_people`).
 */
export function IntegrationConnections({ theme }: IntegrationConnectionsProps) {
  const [connections, setConnections] = useState<Record<string, Connection>>({})
  const popupPoll = useRef<number | null>(null)

  const refresh = useCallback(async (integration: Integration) => {
    setConnections((prev) => ({
      ...prev,
      [integration.key]: { status: 'checking', accounts: [] },
    }))
    const connection = await fetchConnection(integration.statusPath)
    setConnections((prev) => ({ ...prev, [integration.key]: connection }))
  }, [])

  useEffect(() => {
    INTEGRATIONS.forEach((integration) => {
      void refresh(integration)
    })
    return () => {
      if (popupPoll.current) window.clearInterval(popupPoll.current)
    }
  }, [refresh])

  // The OAuth popup is cross-origin, so Opal can't observe it directly. The
  // callback page closes itself once the account is connected; polling for the
  // popup closing is the signal to re-check the connection.
  const startConnectFlow = (integration: Integration) => {
    const popup = window.open(
      integration.connectPath,
      'integration_oauth',
      'popup,width=600,height=760'
    )
    if (!popup) return

    if (popupPoll.current) window.clearInterval(popupPoll.current)
    popupPoll.current = window.setInterval(() => {
      if (!popup.closed) return
      if (popupPoll.current) window.clearInterval(popupPoll.current)
      popupPoll.current = null
      void refresh(integration)
    }, POPUP_POLL_MS)
  }

  // An integration that isn't provisioned for this Opal has nothing to
  // connect to, so its card is dropped rather than shown with a dead button.
  const availableIntegrations = INTEGRATIONS.filter(
    (integration) => connections[integration.key]?.status !== 'unavailable'
  )

  return (
    <ThemeProvider theme={theme}>
      {availableIntegrations.length === 0 && (
        <p css={styles.cardDescription}>
          No integrations are available for this workspace.
        </p>
      )}
      <ul css={styles.cardList}>
        {availableIntegrations.map((integration) => {
          const connection = connections[integration.key]
          const isConnected = connection?.status === 'connected'

          return (
            <li key={integration.key} css={styles.card}>
              <div css={styles.cardBody}>
                <div css={styles.cardTitleRow}>
                  <h2 css={styles.cardName}>{integration.name}</h2>
                  {isConnected && (
                    <Pill
                      color="green"
                      glyph="gem-check-circle-filled"
                      size="small"
                      text="Connected"
                    />
                  )}
                  {connection?.status === 'error' && (
                    <Pill color="gray" size="small" text="Status unavailable" />
                  )}
                </div>
                <p css={styles.cardDescription}>{integration.description}</p>
                {isConnected && connection.accounts.length > 0 && (
                  <p css={styles.cardDescription}>
                    Connected as {connection.accounts.join(', ')}
                  </p>
                )}
              </div>
              <Button
                variant={isConnected ? 'secondary' : 'primary'}
                name={`connect-${integration.key}`}
                buttonText={isConnected ? 'Reconnect' : 'Connect'}
                disabled={connection?.status === 'checking'}
                icon={<Icon glyph="gem-connection" />}
                iconPosition={ButtonIconPosition.leading}
                onClick={() => startConnectFlow(integration)}
              />
            </li>
          )
        })}
      </ul>
    </ThemeProvider>
  )
}
IntegrationConnections.displayName = 'Integrations: IntegrationConnections'
